import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

import {
  DARK_LOGO_URL as darkLogo,
  COMP_INFO,
} from "../../config/siteConfig";
import { BRAND_NAME } from "../../constants/brand";
import AuthBrandPanel from "./AuthBrandPanel";
import { authenticationService } from "../../services/authentication.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  display: "block",
  marginBottom: "8px",
};

const iconStyle = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#c0cad8",
  fontSize: "14px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px 12px 40px",
  border: "1.5px solid #dfe3ec",
  borderRadius: "10px",
  fontSize: "13px",
  color: "#0f172a",
  background: "#f8fafc",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusIn = (e) => {
  e.target.style.borderColor = "var(--btn-bg)";
  e.target.style.boxShadow = "0 0 0 3px rgba(14,106,150,0.15)";
};
const focusOut = (e) => {
  e.target.style.borderColor = "#dfe3ec";
  e.target.style.boxShadow = "none";
};

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isReset = !!token;

  const [email, setEmail] = useState("");
  const [passwords, setPasswords] = useState({
    password: "",
    confirm_password: "",
  });
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [sent, setSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const btnText = btnDisabled
    ? "Please Wait..."
    : isReset
      ? "Reset Password"
      : "Send Reset Link";

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setErrMsg(null);

    if (passwords.password.length < 6) {
      setErrMsg("Password must be at least 6 characters");
      return;
    }
    if (passwords.password !== passwords.confirm_password) {
      setErrMsg("Passwords do not match");
      return;
    }

    try {
      setBtnDisabled(true);
      const res = await authenticationService.resetPassword({
        method: "verify-token",
        token,
        confirm_password: passwords.confirm_password,
      });
      if (res.success) {
        setResetDone(true);
        toast.success(res.message || "Password reset successfully");
      } else {
        setErrMsg(res.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setBtnDisabled(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg(null);

    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setErrMsg("Please enter a valid email address");
      return;
    }

    try {
      setBtnDisabled(true);
      const res = await authenticationService.changePasswordLink({
        email: trimmed,
        method: "get-email-link",
      });

      if (res.success) {
        setSent(true);
        toast.success(res.message || "Reset link sent to your email");
      } else {
        setErrMsg(res.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setBtnDisabled(false);
    }
  };

  return (
    <>
      <style>{`
        .login_mobile_brand { display: none; }
        @media (max-width: 767.98px) {
          .auth_login .auth_bg { height: 100dvh; }
          .auth_login .auth_bg .auth_content {
            width: 100%; height: 100%; border-radius: 0; box-shadow: none; background: transparent;
            flex-direction: column; overflow-y: auto; padding: 32px 16px;
          }
          .auth_login .auth_bg .auth_content .flex50 { max-width: 100%; flex: 0 0 auto; }
          .auth_login .auth_right {
            height: auto !important; overflow: visible !important; padding: 28px 22px !important;
            border-radius: 20px; box-shadow: 0 20px 60px rgba(14,106,150,0.15);
            max-width: 440px; margin: 0 auto;
          }
          .auth_login .login_mobile_brand {
            display: flex; flex-direction: column; align-items: center; text-align: center;
            width: 100%; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #eef0f8;
          }
          .auth_login .login_mobile_brand a { display: inline-flex; align-items: center; gap: 10px; text-decoration: none; }
          .auth_login .login_mobile_brand img { height: 36px; width: auto; object-fit: contain; }
          .auth_login .login_mobile_name { display: block; font-size: 18px; font-weight: 700; line-height: 1.1; color: #0f172a; text-align: left; }
          .auth_login .login_mobile_sub { display: block; font-size: 12px; color: #64748b; margin-top: 2px; text-align: left; }
          .auth_login .auth_right form input { font-size: 16px !important; }
        }
      `}</style>
      <div className="auth_login">
        <div className="auth_bg">
          <div className="auth_content">
            {/* Left panel — hidden on mobile */}
            <div className="flex50 d-none d-md-flex">
              <AuthBrandPanel ctaText="Remembered your password?" ctaLabel="Back to login" ctaTo="/login" />
            </div>

            {/* Right panel — full width on mobile */}
            <div className="flex50" style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div
                className="auth_right"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  overflowY: "auto",
                  padding: "32px 20px",
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(24px)",
                }}
              >
                <div style={{ width: "100%", maxWidth: "400px" }}>
                  {/* Mobile-only: brand header (dark logo on white card) */}
                  <div className="login_mobile_brand d-md-none">
                    <Link to="/" aria-label={`${BRAND_NAME} home`}>
                      <img src={darkLogo} alt="" />
                      <span>
                        <span className="login_mobile_name">{BRAND_NAME}</span>
                        <span className="login_mobile_sub">{COMP_INFO.NAME}</span>
                      </span>
                    </Link>
                  </div>

                  {/* Page header */}
                  <div style={{ marginBottom: "24px" }} className="text-center">
                    <h4
                      style={{
                        fontWeight: "700",
                        color: "#0f172a",
                        fontSize: "22px",
                        marginBottom: "4px",
                      }}
                    >
                      {isReset ? "Reset Password" : "Forgot Password?"}
                    </h4>
                    <p
                      style={{ color: "#64748b", fontSize: "13px", margin: 0 }}
                    >
                      {isReset
                        ? "Choose a new password for your account"
                        : "Enter your registered email and we'll send you a reset link"}
                    </p>
                  </div>

                  {resetDone || sent ? (
                    <div className="text-center">
                      <div
                        style={{
                          background: "#f0faf4",
                          border: "1.5px solid #bbf7d0",
                          borderRadius: "10px",
                          padding: "14px",
                          color: "#15803d",
                          fontSize: "13px",
                          marginBottom: "20px",
                        }}
                      >
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {resetDone ? (
                          "Your password has been reset. You can now log in with your new password."
                        ) : (
                          <>
                            If this email is registered, a password reset link
                            has been sent to <b>{email.trim()}</b>.
                          </>
                        )}
                      </div>
                      {sent && !resetDone && (
                        <button
                          type="button"
                          onClick={() => setSent(false)}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "12px",
                            color: "var(--btn-bg)",
                            fontWeight: "600",
                            marginBottom: "16px",
                          }}
                        >
                          Use a different email
                        </button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={isReset ? handleResetSubmit : handleSubmit}>
                      {/* Error */}
                      {errMsg && (
                        <div
                          style={{
                            background: "#fff4f4",
                            border: "1.5px solid #fecaca",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            color: "#c45252",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "20px",
                          }}
                        >
                          <i
                            className="bi bi-exclamation-circle-fill"
                            style={{ flexShrink: 0 }}
                          ></i>
                          {errMsg}
                        </div>
                      )}

                      {isReset ? (
                        <>
                          <div style={{ marginBottom: "14px" }}>
                            <label style={labelStyle}>New Password</label>
                            <div style={{ position: "relative" }}>
                              <i className="bi bi-lock" style={iconStyle}></i>
                              <input
                                type="password"
                                name="password"
                                autoComplete="new-password"
                                value={passwords.password}
                                placeholder="Enter new password"
                                onChange={(e) =>
                                  setPasswords({
                                    ...passwords,
                                    password: e.target.value,
                                  })
                                }
                                style={inputStyle}
                                onFocus={focusIn}
                                onBlur={focusOut}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: "24px" }}>
                            <label style={labelStyle}>Confirm Password</label>
                            <div style={{ position: "relative" }}>
                              <i
                                className="bi bi-lock-fill"
                                style={iconStyle}
                              ></i>
                              <input
                                type="password"
                                name="confirm_password"
                                autoComplete="new-password"
                                value={passwords.confirm_password}
                                placeholder="Confirm new password"
                                onChange={(e) =>
                                  setPasswords({
                                    ...passwords,
                                    confirm_password: e.target.value,
                                  })
                                }
                                style={inputStyle}
                                onFocus={focusIn}
                                onBlur={focusOut}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ marginBottom: "24px" }}>
                          <label style={labelStyle}>Email</label>
                          <div style={{ position: "relative" }}>
                            <i className="bi bi-envelope" style={iconStyle}></i>
                            <input
                              type="email"
                              name="email"
                              id="email"
                              value={email}
                              placeholder="Enter your registered email"
                              onChange={(e) => setEmail(e.target.value)}
                              style={inputStyle}
                              onFocus={focusIn}
                              onBlur={focusOut}
                            />
                          </div>
                        </div>
                      )}

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={btnDisabled}
                        style={{
                          width: "100%",
                          padding: "13px",
                          background: btnDisabled
                            ? "#9cc7c2"
                            : "var(--btn-bg)",
                          color: "white",
                          border: "none",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: "700",
                          letterSpacing: "0.3px",
                          cursor: btnDisabled ? "not-allowed" : "pointer",
                          transition: "all 0.2s",
                          marginBottom: "20px",
                          boxShadow: btnDisabled
                            ? "none"
                            : "0 4px 14px rgba(14,106,150,0.25)",
                        }}
                      >
                        <i
                          className={`bi ${btnDisabled ? "bi-hourglass-split" : isReset ? "bi-key" : "bi-send"} me-2`}
                        ></i>
                        {btnText}
                      </button>
                    </form>
                  )}

                  {/* Back to login */}
                  <div style={{ textAlign: "center" }}>
                    <Link to="/login" style={{ textDecoration: "none" }}>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--btn-bg)",
                          fontWeight: "600",
                        }}
                      >
                        <i
                          className="bi bi-arrow-left me-1"
                          style={{ fontSize: "11px" }}
                        ></i>
                        Back to Login
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
